import type { AtlasAgentMessage } from '@atlas/api-contract';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import {
  createAgentBridge,
  type AgentBridge,
  type AgentMessageVerdict,
} from './agent-bridge';

export interface UseAgentBridgeOptions {
  /** The iframe hosting the agent. May be `null` on the first render. */
  readonly frameRef: RefObject<HTMLIFrameElement | null>;
  /** Called for every authenticated, well-shaped agent message. */
  readonly onMessage?: (message: AtlasAgentMessage) => void;
}

export interface UseAgentBridgeResult
  extends Pick<
    AgentBridge,
    | 'send'
    | 'setMode'
    | 'navigate'
    | 'renderPins'
    | 'highlightPin'
    | 'scrollToPin'
    | 'replay'
    | 'notifyAccessRefreshed'
  > {
  /** True once `atlas:ready` has arrived from the current document. */
  readonly ready: boolean;
  /**
   * Tell the bridge the framed document is being replaced. Call it from the
   * iframe's `load` handler and before any deliberate reload.
   */
  readonly reset: () => void;
}

/**
 * React's half of the agent channel: one window listener, and typed senders.
 *
 * Three things this hook exists to get right, all of which are easy to get
 * wrong with a naive `useEffect`:
 *
 * - **One listener, for the life of the viewer.** It is registered on mount and
 *   removed on unmount, and nothing in its dependency array can churn — the
 *   caller's `onMessage` is read through a ref rather than captured, so passing
 *   an inline arrow function does not re-subscribe on every render and cannot
 *   leak a listener per keystroke.
 * - **The iframe is allowed to change underneath it.** Authentication reads
 *   `frameRef.current?.contentWindow` at the moment the event arrives, so a
 *   reload, a `src` change or a remount that hands React a brand-new element
 *   are all handled without touching the subscription. A bridge that captured
 *   `contentWindow` once would silently start rejecting every message after the
 *   first navigation.
 * - **Senders are safe to call before the agent boots.** The underlying bridge
 *   queues by message type and flushes on `atlas:ready`, so a comment-mode
 *   toggle during the first second of a load is honoured rather than lost.
 *
 * Authentication itself lives in `agent-bridge.ts`, with the block comment
 * explaining why it is `event.source` identity and not `event.origin`. The
 * short version: `event.origin` is the literal string `"null"` for a sandboxed
 * frame with no `allow-same-origin`, and is therefore worthless as a credential.
 */
export const useAgentBridge = ({
  frameRef,
  onMessage,
}: UseAgentBridgeOptions): UseAgentBridgeResult => {
  const [ready, setReady] = useState(false);

  /* Read at event time so the listener never has to be rebuilt. */
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const bridgeRef = useRef<AgentBridge | null>(null);

  bridgeRef.current ??= createAgentBridge({
    getAgentWindow: () => frameRef.current?.contentWindow ?? null,
    onMessage: (message) => {
      onMessageRef.current?.(message);
    },
    onReadyChange: setReady,
  });

  const bridge = bridgeRef.current;

  useEffect(() => {
    const handleMessage = (event: MessageEvent): AgentMessageVerdict =>
      bridge.receive(event);

    /*
     * A plain listener on `window`, not on the iframe: `message` events are
     * delivered to the receiving *window*, and there is no per-frame event to
     * subscribe to. Everything on the page therefore reaches this handler,
     * which is exactly why `bridge.receive` authenticates before it trusts.
     */
    const listener = (event: MessageEvent): void => {
      handleMessage(event);
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
      /*
       * Readiness belongs to a live document. Dropping it on unmount means a
       * viewer that is mounted again starts by waiting for the agent instead of
       * posting into a window that was torn down.
       */
      bridge.reset();
    };
  }, [bridge]);

  return {
    ready,
    reset: useCallback(() => {
      bridge.reset();
    }, [bridge]),
    send: useCallback((message) => bridge.send(message), [bridge]),
    setMode: useCallback((mode) => bridge.setMode(mode), [bridge]),
    navigate: useCallback(
      (screenPath) => bridge.navigate(screenPath),
      [bridge],
    ),
    renderPins: useCallback(
      (pins, showResolved) => bridge.renderPins(pins, showResolved),
      [bridge],
    ),
    highlightPin: useCallback(
      (threadId) => bridge.highlightPin(threadId),
      [bridge],
    ),
    scrollToPin: useCallback(
      (threadId) => bridge.scrollToPin(threadId),
      [bridge],
    ),
    replay: useCallback(
      (threadId, gesturePath, expectedScreenKey) =>
        bridge.replay(threadId, gesturePath, expectedScreenKey),
      [bridge],
    ),
    notifyAccessRefreshed: useCallback(
      () => bridge.notifyAccessRefreshed(),
      [bridge],
    ),
  };
};
